import React, { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { theme } from '../styles/theme'
import { translateAuthError } from '../services/auth'
import axios from 'axios'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api'

// ─── Styled Components (matches LoginScreen) ──────────────────────────────────

const Container = styled.div`
  position: relative;
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xl};
  background: linear-gradient(160deg, ${theme.colors.background} 0%, ${theme.colors.light} 45%, ${theme.colors.lightHover} 100%);
  overflow: hidden;

  @media (max-width: 768px) {
    padding: ${theme.spacing.lg};
  }

  @media (max-width: 480px) {
    padding: ${theme.spacing.md};
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    filter: blur(0px);
    opacity: 0.35;
    z-index: 0;
  }

  &::before {
    top: -140px;
    right: -140px;
    background: radial-gradient(circle, ${theme.colors.honeycomb} 0%, transparent 70%);
  }

  &::after {
    bottom: -160px;
    left: -160px;
    background: radial-gradient(circle, ${theme.colors.primaryLight} 0%, transparent 70%);
  }
`

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 460px;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.xxlarge};
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid ${theme.colors.borderLight};
  box-shadow: ${theme.shadows.xl};
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    max-width: 100%;
    padding: ${theme.spacing.lg};
  }

  @media (max-width: 480px) {
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.large};
  }
`

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
`

const LogoMark = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.large};
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.white};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
  box-shadow: 0 10px 24px rgba(246, 180, 0, 0.35);
`

const Title = styled.h1`
  font-size: ${theme.typography.fontSize.xxlarge};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text};
  text-align: center;

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.xlarge};
  }
`

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.small};
  color: ${theme.colors.textSecondary};
  text-align: center;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

const Label = styled.label`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textSecondary};
  letter-spacing: 0.3px;
  text-transform: uppercase;
`

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.large};
  font-size: ${theme.typography.fontSize.medium};
  background: ${theme.colors.backgroundSecondary};
  color: ${theme.colors.text};
  transition: ${theme.transitions.normal};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 4px rgba(246, 180, 0, 0.15);
    background: ${theme.colors.white};
  }

  &::placeholder {
    color: ${theme.colors.textTertiary};
  }
`

const Button = styled.button<{ disabled?: boolean; isLoading?: boolean; variant?: 'primary' | 'outline' }>`
  margin-top: ${theme.spacing.xs};
  padding: ${theme.spacing.md};
  width: 100%;
  background: ${props =>
    props.variant === 'outline'
      ? 'transparent'
      : props.disabled || props.isLoading
      ? theme.colors.border
      : `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`};
  color: ${props => props.variant === 'outline' ? theme.colors.primaryDark : theme.colors.white};
  border: ${props => props.variant === 'outline' ? `1.5px solid ${theme.colors.primary}` : 'none'};
  border-radius: ${theme.borderRadius.large};
  font-size: ${theme.typography.fontSize.medium};
  font-weight: ${theme.typography.fontWeight.bold};
  cursor: ${props => (props.disabled || props.isLoading ? 'not-allowed' : 'pointer')};
  transition: ${theme.transitions.normal};
  box-shadow: ${props => props.variant === 'outline' ? 'none' : '0 12px 24px rgba(209, 138, 0, 0.2)'};

  &:hover {
    transform: ${props => (props.disabled || props.isLoading ? 'none' : 'translateY(-1px)')};
    box-shadow: ${props =>
      props.disabled || props.isLoading
        ? 'none'
        : props.variant === 'outline'
        ? 'none'
        : '0 16px 28px rgba(209, 138, 0, 0.28)'};
    background: ${props =>
      props.variant === 'outline' ? 'rgba(246, 180, 0, 0.08)' : undefined};
  }
`

const ErrorMessage = styled.div`
  color: ${theme.colors.danger};
  font-size: ${theme.typography.fontSize.small};
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.medium};
`

const BackLink = styled.button`
  display: inline;
  padding: 0;
  border: none;
  background: none;
  color: ${theme.colors.primaryDark};
  font-size: ${theme.typography.fontSize.small};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.primary};
    text-decoration: underline;
  }
`

// ─── Success State ────────────────────────────────────────────────────────────

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.md};
  text-align: center;
  padding: ${theme.spacing.lg} 0;
`

const SuccessEmoji = styled.div`
  font-size: 64px;
  line-height: 1;
`

const SuccessTitle = styled.h2`
  font-size: ${theme.typography.fontSize.xxlarge};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text};
  margin: 0;
`

const SuccessText = styled.p`
  font-size: ${theme.typography.fontSize.medium};
  color: ${theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;
`

// ─── Component ────────────────────────────────────────────────────────────────

function RegisterScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [accountName, setAccountName] = useState('')
  const [ownerName, setOwnerName] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [ownerPassword, setOwnerPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setError('')

    if (!accountName.trim() || !ownerName.trim() || !ownerEmail.trim() || !ownerPassword.trim()) {
      setError(t('register.allFieldsRequired'))
      return
    }

    if (ownerPassword.length < 6) {
      setError(t('auth.passwordMinLength'))
      return
    }

    if (ownerPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'))
      return
    }

    try {
      setIsSubmitting(true)
      await axios.post(`${API_BASE_URL}/auth/register`, {
        accountName: accountName.trim(),
        ownerName: ownerName.trim(),
        ownerEmail: ownerEmail.trim(),
        ownerPassword,
      })
      setSuccess(true)
    } catch (err: any) {
      setError(translateAuthError(err, 'register.failed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Container>
      <Card>
        <CardHeader>
          <LogoMark>🐝</LogoMark>
          <Title>{success ? t('register.accountCreated') : t('register.title')}</Title>
          {!success && <Subtitle>{t('register.subtitle')}</Subtitle>}
        </CardHeader>

        {success ? (
          <SuccessContainer>
            <SuccessEmoji>🎉</SuccessEmoji>
            <SuccessTitle>{t('register.successTitle')}</SuccessTitle>
            <SuccessText>{t('register.successText')}</SuccessText>
            <Button
              type="button"
              onClick={() => navigate('/login')}
              style={{ marginTop: theme.spacing.md }}
            >
              {t('register.goToSignIn')}
            </Button>
          </SuccessContainer>
        ) : (
          <>
            <Form onSubmit={handleSubmit}>
              <InputGroup>
                <Label htmlFor="accountName">{t('register.accountName')}</Label>
                <Input
                  id="accountName"
                  type="text"
                  value={accountName}
                  onChange={e => setAccountName(e.target.value)}
                  placeholder={t('register.accountNamePlaceholder')}
                  autoComplete="organization"
                  required
                />
              </InputGroup>

              <InputGroup>
                <Label htmlFor="ownerName">{t('register.yourName')}</Label>
                <Input
                  id="ownerName"
                  type="text"
                  value={ownerName}
                  onChange={e => setOwnerName(e.target.value)}
                  placeholder={t('register.yourNamePlaceholder')}
                  autoComplete="name"
                  required
                />
              </InputGroup>

              <InputGroup>
                <Label htmlFor="ownerEmail">{t('auth.email')}</Label>
                <Input
                  id="ownerEmail"
                  type="email"
                  value={ownerEmail}
                  onChange={e => setOwnerEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </InputGroup>

              <InputGroup>
                <Label htmlFor="ownerPassword">{t('auth.password')}</Label>
                <Input
                  id="ownerPassword"
                  type="password"
                  value={ownerPassword}
                  onChange={e => setOwnerPassword(e.target.value)}
                  placeholder={t('register.passwordPlaceholder')}
                  autoComplete="new-password"
                  required
                  minLength={6}
                />
              </InputGroup>

              <InputGroup>
                <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder={t('register.confirmPasswordPlaceholder')}
                  autoComplete="new-password"
                  required
                  minLength={6}
                />
              </InputGroup>

              {error && <ErrorMessage>{error}</ErrorMessage>}

              <Button type="submit" disabled={isSubmitting} isLoading={isSubmitting}>
                {isSubmitting ? t('register.creating') : t('register.title')}
              </Button>
            </Form>

            <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '14px', color: theme.colors.textSecondary }}>
              {t('register.haveAccount')}{' '}
              <BackLink type="button" onClick={() => navigate('/login')}>
                {t('auth.backToLoginArrow')}
              </BackLink>
            </p>
          </>
        )}
      </Card>
    </Container>
  )
}

export default RegisterScreen
